import React, { useEffect, useState } from "react";
import config from "../../config";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import { motion } from "framer-motion";

const company = JSON.parse(sessionStorage.getItem("companyDetails") || "{}");
const Companycurrency = company?.currency ?? "";

const EMPTY_ROW = { itemName: "", qty: "", rate: "", amount: 0 };

export default function PurchaseReport() {
  const token = sessionStorage.getItem("token");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");

  // Details modal (read-only line items)
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const fetchReport = async (from, to) => {
    setLoading(true);
    try {
      let url = `${config.API_BASE_URL}/home/GetPurchasereport`;
      const params = [];
      if (from) params.push(`fromdate=${from}`);
      if (to) params.push(`todate=${to}`);
      params.push(`id=${company?.id}`);
      if (params.length > 0) url += `?${params.join("&")}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const text = await res.text();
      setData(text ? JSON.parse(text) : null);
    } catch (e) {
      console.error(e);
      notify("Failed to load report", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(null, null);
  }, []);

  const openDetails = (purchase) => {
    setSelectedPurchase(purchase);
    setItemModalOpen(true);
  };

  // Pulls the canonical purchase row from the server (single endpoint) before
  // opening the edit dialog. The list endpoint groups + truncates fields, so
  // we re-fetch to make sure we edit the latest values.
  const openEdit = async (purchase) => {
    setEditLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/GetPurchaseById/${purchase.purchaseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to load purchase");
      const detail = await res.json();

      // Date input needs YYYY-MM-DD format.
      const billDate = detail.transactionDate
        ? new Date(detail.transactionDate).toISOString().slice(0, 10)
        : "";

      setEditForm({
        purchaseId: detail.purchaseId,
        vendorName: detail.vendorName || "",
        billNo: detail.refNo || "", // RefNo is reused as BillNo in ledger
        billDate,
        remark: detail.remark || "",
        cardnumber: detail.cardNumber || "",
        cardtype: detail.cardType || "",
        refno: detail.refNo || "",
        mode: detail.mode || "Purchase",
      });
      setEditItems(
        (detail.items || []).map((it) => ({
          itemName: it.itemName || "",
          qty: String(it.qty ?? ""),
          rate: String(it.rate ?? ""),
          amount: Number(it.amount || 0),
          storeItemId: it.storeItemId || null,
        }))
      );
      setEditOpen(true);
    } catch (e) {
      notify(e.message || "Failed to open edit", "error");
    } finally {
      setEditLoading(false);
    }
  };

  const recalcAmount = (row) => {
    const qty = parseFloat(row.qty) || 0;
    const rate = parseFloat(row.rate) || 0;
    return { ...row, amount: qty * rate };
  };

  const handleEditItemChange = (i, field, value) => {
    const updated = [...editItems];
    updated[i] = recalcAmount({ ...updated[i], [field]: value });
    setEditItems(updated);
  };

  const addEditRow = () => setEditItems([...editItems, { ...EMPTY_ROW }]);
  const removeEditRow = (i) => {
    if (editItems.length === 1) {
      setEditItems([{ ...EMPTY_ROW }]);
      return;
    }
    setEditItems(editItems.filter((_, idx) => idx !== i));
  };

  const editTotal = editItems.reduce((s, x) => s + (x.amount || 0), 0);

  const saveEdit = async () => {
    if (!editForm?.vendorName?.trim()) {
      notify("Vendor name is required", "warning");
      return;
    }
    const validRows = editItems.filter(
      (r) =>
        r.itemName.trim() && parseFloat(r.qty) > 0 && parseFloat(r.rate) > 0
    );
    if (validRows.length === 0) {
      notify("Add at least one valid item", "warning");
      return;
    }

    const payload = {
      purchaseId: editForm.purchaseId,
      vendorName: editForm.vendorName.trim(),
      billNo: editForm.billNo.trim(),
      billDate: editForm.billDate,
      remark: editForm.remark.trim(),
      cardnumber: editForm.cardnumber || null,
      cardtype: editForm.cardtype || null,
      refno: editForm.refno || null,
      Companyid: company?.id,
      items: validRows.map((r) => ({
        itemName: r.itemName.trim(),
        qty: parseFloat(r.qty) || 0,
        rate: parseFloat(r.rate) || 0,
        amount: parseFloat(r.amount) || 0,
        storeItemId: r.storeItemId || 0,
      })),
    };

    try {
      setEditing(true);
      const res = await fetch(
        `${config.API_BASE_URL}/home/UpdatePurchase`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const body = await res.json();
      if (body.status !== "success") throw new Error(body.message || "Update failed");
      notify("Purchase updated successfully");
      setEditOpen(false);
      fetchReport(fromDate, toDate);
    } catch (e) {
      notify(e.message || "Update failed", "error");
    } finally {
      setEditing(false);
    }
  };

  const deletePurchase = async (purchase) => {
    const ok = window.confirm(
      `Delete Purchase #${purchase.purchaseId} from ${purchase.vendorName}?\nStock changes will be reversed.`
    );
    if (!ok) return;
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/DeletePurchase/${purchase.purchaseId}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const body = await res.json();
      if (body.status !== "success") throw new Error(body.message || "Delete failed");
      notify("Purchase deleted");
      fetchReport(fromDate, toDate);
    } catch (e) {
      notify(e.message || "Delete failed", "error");
    }
  };

  const filtered = data?.purchases?.filter((p) =>
    Object.values(p)
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  if (loading)
    return (
      <Box textAlign="center" mt={8}>
        <CircularProgress />
      </Box>
    );

  if (!data) return <Typography align="center">No data</Typography>;

  return (
    <Box p={4}>
      <Typography variant="h4" fontWeight="bold" mb={3} textAlign="center">
        🧾 Purchase Report
      </Typography>

      {/* Summary */}
      <Grid container spacing={3} mb={4}>
        {[
          { label: "Date Range Total", val: data.dateRangeTotal },
          { label: "Overall Total", val: data.overallTotal },
        ].map((x, i) => (
          <Grid item xs={12} md={6} key={i}>
            <motion.div whileHover={{ scale: 1.03 }}>
              <Paper sx={{ p: 3, textAlign: "center" }}>
                <Typography>{x.label}</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {Companycurrency}
                  {Number(x.val || 0).toFixed(2)}
                </Typography>
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <TextField
          type="date"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          size="small"
        />
        <TextField
          type="date"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          size="small"
        />
        <Button variant="contained" onClick={() => fetchReport(fromDate, toDate)}>
          Apply
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setFromDate("");
            setToDate("");
            fetchReport(null, null);
          }}
        >
          Clear
        </Button>
        <TextField
          label="Search (vendor, item, bill no)"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
      </Box>

      {/* Table */}
      <Paper>
        <Table>
          <TableHead>
            <TableRow sx={{ background: "#0a2540" }}>
              {["#", "PurchaseId", "Vendor", "Date", "Mode", "Total", "Actions"].map(
                (h) => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 700 }}>
                    {h}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {(!filtered || filtered.length === 0) && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  No purchases found
                </TableCell>
              </TableRow>
            )}
            {filtered?.map((p, i) => (
              <TableRow key={i} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell>#{p.purchaseId}</TableCell>
                <TableCell>{p.vendorName}</TableCell>
                <TableCell>{new Date(p.transactionDate).toLocaleString('en-GB')}</TableCell>
                <TableCell>{p.mode}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  {Companycurrency}
                  {Number(p.ledgerTotalAmount || 0).toFixed(2)}
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => openDetails(p)}>
                      <VisibilityIcon fontSize="small" color="info" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => openEdit(p)}
                      disabled={editLoading}
                    >
                      <EditIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => deletePurchase(p)}>
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Details Modal */}
      <Dialog
        open={itemModalOpen}
        onClose={() => setItemModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Purchase #{selectedPurchase?.purchaseId} — {selectedPurchase?.vendorName}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              <b>Date:</b>{" "}
              {selectedPurchase?.transactionDate
                ? new Date(selectedPurchase.transactionDate).toLocaleString('en-GB')
                : "—"}
            </Typography>
            <Typography variant="body2">
              <b>Mode:</b> {selectedPurchase?.mode || "—"}
              {selectedPurchase?.refNo ? ` (Ref: ${selectedPurchase.refNo})` : ""}
            </Typography>
            <Typography variant="body2">
              <b>Remark:</b> {selectedPurchase?.remark || "—"}
            </Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "#f5f5f5" }}>
                <TableCell>Item</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(selectedPurchase?.items || []).map((x, i) => (
                <TableRow key={i}>
                  <TableCell>{x.itemName}</TableCell>
                  <TableCell align="right">{x.qty}</TableCell>
                  <TableCell align="right">{Number(x.rate || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    {Companycurrency}
                    {Number(x.amount || 0).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>
                  Total
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {Companycurrency}
                  {Number(selectedPurchase?.ledgerTotalAmount || 0).toFixed(2)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog
        open={editOpen}
        onClose={() => !editing && setEditOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Purchase #{editForm?.purchaseId}</DialogTitle>
        <DialogContent dividers>
          {editForm && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Vendor Name"
                    fullWidth
                    size="small"
                    value={editForm.vendorName}
                    onChange={(e) =>
                      setEditForm({ ...editForm, vendorName: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Bill No"
                    fullWidth
                    size="small"
                    value={editForm.billNo}
                    onChange={(e) =>
                      setEditForm({ ...editForm, billNo: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    type="date"
                    label="Bill Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                    value={editForm.billDate}
                    onChange={(e) =>
                      setEditForm({ ...editForm, billDate: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Remark"
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    value={editForm.remark}
                    onChange={(e) =>
                      setEditForm({ ...editForm, remark: e.target.value })
                    }
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Items
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: "#f5f5f5" }}>
                    <TableCell>Item Name</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editItems.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ minWidth: 200 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.itemName}
                          onChange={(e) =>
                            handleEditItemChange(i, "itemName", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ width: 90 }}>
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: "any" }}
                          value={row.qty}
                          onChange={(e) =>
                            handleEditItemChange(i, "qty", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ width: 110 }}>
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: "any" }}
                          value={row.rate}
                          onChange={(e) =>
                            handleEditItemChange(i, "rate", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {Companycurrency}
                        {Number(row.amount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => removeEditRow(i)}>
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addEditRow}
                      >
                        Add Item
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>
                      Total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {Companycurrency}
                      {editTotal.toFixed(2)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editing}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveEdit} disabled={editing}>
            {editing ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
